import * as React from "react"
import { Loader2 } from "lucide-react"
import { cn } from "../../lib/utils"
import { cva, type VariantProps } from "class-variance-authority"

const spinnerVariants = cva(
  "animate-spin text-muted-foreground",
  {
    variants: {
      size: {
        default: "h-6 w-6",
        sm: "h-4 w-4",
        lg: "h-10 w-10",
        xl: "h-14 w-14",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
)

interface SpinnerProps
  extends React.HTMLAttributes<SVGElement>,
    VariantProps<typeof spinnerVariants> {}

const Spinner = React.forwardRef<SVGSVGElement, SpinnerProps>(
  ({ className, size, ...props }, ref) => {
    return (
      <Loader2
        ref={ref}
        className={cn(spinnerVariants({ size }), className)}
        {...props}
      />
    )
  }
)

Spinner.displayName = "Spinner"

// Spin wrapper for backwards compatibility with antd Spin API
interface SpinProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "small" | "default" | "large"
  spinning?: boolean
  children?: React.ReactNode
}

const Spin = React.forwardRef<HTMLDivElement, SpinProps>(
  ({ className, size = "default", spinning = true, children, ...props }, ref) => {
    const spinnerSize = size === "small" ? "sm" : size === "large" ? "lg" : "default"

    if (children) {
      return (
        <div ref={ref} className={cn("relative", className)} {...props}>
          {spinning && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10">
              <Spinner size={spinnerSize} />
            </div>
          )}
          <div className={cn(spinning && "opacity-50 pointer-events-none")}>
            {children}
          </div>
        </div>
      )
    }

    if (!spinning) return null

    return (
      <div ref={ref} className={cn("flex items-center justify-center", className)} {...props}>
        <Spinner size={spinnerSize} />
      </div>
    )
  }
)

Spin.displayName = "Spin"

export { Spinner, Spin, spinnerVariants }
